import React, { useState } from "react";
import { useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";
import { CalendarIcon, Plus, Trash, Gift } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/utils";

const AdminDebugPanel: React.FC = () => {
  const { customers, rewards, hasPermission, getCustomerRewards, loadInitialData, addCustomerVisit, redeemReward } = useApp();
  const [selectedCustomerId, setSelectedCustomerId] = useState<string>("");
  const [visitDate, setVisitDate] = useState<Date>(new Date());
  const [rewardDate, setRewardDate] = useState<Date>(new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [rewardDatePickerOpen, setRewardDatePickerOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("add-visit");
  const [selectedRewardId, setSelectedRewardId] = useState<string>("");

  // Return early if not an admin
  if (!hasPermission("manage_rewards")) {
    return null;
  }

  // Check if customers array is undefined or empty
  const hasCustomers = Array.isArray(customers) && customers.length > 0;
  
  const selectedCustomer = hasCustomers ? customers.find((customer) => customer.id === selectedCustomerId) : undefined;
  const availableRewards = selectedCustomer ? getCustomerRewards(selectedCustomer.id) : [];

  const handleAddVisit = async () => {
    if (selectedCustomerId) {
      // Pass the selected date to the addCustomerVisit function
      await addCustomerVisit(selectedCustomerId, visitDate);
    }
  };

  const handleRedeemReward = async () => {
    if (selectedCustomerId && selectedRewardId) {
      // Pass true to bypass eligibility check for admin testing
      await redeemReward(selectedCustomerId, selectedRewardId, true);
    }
  };

  // If customers data is not available yet, show loading or message
  if (!hasCustomers) {
    return (
      <Card className="border-red-300 shadow-md my-4">
        <CardHeader className="bg-red-50">
          <CardTitle className="text-red-700 flex items-center gap-2">
            <span className="text-red-600 bg-red-100 p-1 rounded-md text-xs font-mono">DEBUG</span>
            Admin Rewards Testing Panel
          </CardTitle>
          <CardDescription>
            Loading customer data or no customers available yet...
          </CardDescription>
        </CardHeader>
        <CardContent className="pt-4">
          <p>Please add at least one customer before using the debug panel.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-t-4 border-t-restaurant-primary shadow-lg">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center text-lg">
          <Gift className="h-5 w-5 mr-2 text-restaurant-primary" />
          Admin Rewards Testing Panel
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground mb-4">
          Test the rewards system by adding visits with custom dates
        </p>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-muted/50 mb-4">
            <TabsTrigger value="add-visit" className="data-[state=active]:bg-white data-[state=active]:text-restaurant-primary">Add Custom Visit</TabsTrigger>
            <TabsTrigger value="redeem-reward" className="data-[state=active]:bg-white data-[state=active]:text-restaurant-primary">Redeem Reward</TabsTrigger>
          </TabsList>

          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="customer-select" className="text-sm font-medium">
                Select Customer
              </label>
              <Select
                value={selectedCustomerId}
                onValueChange={setSelectedCustomerId}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map((customer) => (
                    <SelectItem key={customer.id} value={customer.id}>
                      {customer.name || customer.phoneNumber}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <TabsContent value="add-visit" className="space-y-4 mt-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Visit Date</label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant={"outline"}
                      className={cn(
                        "w-full justify-start text-left font-normal",
                        !visitDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {visitDate ? format(visitDate, "PPP") : "Select date"}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={visitDate}
                      onSelect={(date) => date && setVisitDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>

              <Button 
                onClick={handleAddVisit} 
                disabled={!selectedCustomerId}
                className="w-full bg-restaurant-primary hover:bg-restaurant-primary/90"
              >
                Add Visit
              </Button>

              {selectedCustomer && (
                <div className="pt-4 mt-4 border-t">
                  <h3 className="font-medium mb-2">Customer Visit History</h3>
                  {selectedCustomer.visits.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No visits yet</p>
                  ) : (
                    <div className="space-y-2">
                      {selectedCustomer.visits
                        .slice()
                        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                        .map((visit) => (
                          <div key={visit.id} className="text-sm flex justify-between items-center p-2 bg-muted rounded-md">
                            <span>{format(new Date(visit.date), "PPP")}</span>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-100"
                              onClick={async () => {
                                try {
                                  await supabase.from('visits').delete().eq('id', visit.id);
                                  await loadInitialData();
                                  toast.success("Visit deleted");
                                } catch (error) {
                                  toast.error("Failed to delete visit");
                                }
                              }}
                            >
                              <Trash className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="redeem-reward" className="mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Reward</label>
                  <Select
                    value={selectedRewardId}
                    onValueChange={setSelectedRewardId}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select reward..." />
                    </SelectTrigger>
                    <SelectContent>
                      {rewards.map((reward) => (
                        <SelectItem key={reward.id} value={reward.id}>
                          {reward.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleRedeemReward} 
                  disabled={!selectedCustomerId || !selectedRewardId}
                  className="w-full bg-restaurant-primary hover:bg-restaurant-primary/90"
                >
                  Redeem Reward
                </Button>
              </div>
            </TabsContent>

            {selectedCustomer && (
              <div className="pt-4 mt-4 border-t">
                <h3 className="font-medium mb-2">Customer Redemption History</h3>
                {selectedCustomer.redeemedRewards.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No redemptions yet</p>
                ) : (
                  <div className="space-y-2">
                    {selectedCustomer.redeemedRewards
                      .slice()
                      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                      .map((redemption, index) => {
                        const reward = rewards.find(r => r.id === redemption.rewardId);
                        return (
                          <div key={index} className="text-sm flex justify-between items-center p-2 bg-muted rounded-md">
                            <div>
                              <span className="font-medium">{reward?.name || "Unknown Reward"}</span>
                              <span className="text-xs text-muted-foreground ml-2">
                                {format(new Date(redemption.date), "PPP")}
                              </span>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              className="h-6 px-2 text-red-500 hover:text-red-700 hover:bg-red-100"
                              onClick={async () => {
                                try {
                                  // Delete by customer ID + reward ID + date
                                  const { error } = await supabase
                                    .from('redeemed_rewards')
                                    .delete()
                                    .eq('customer_id', selectedCustomer.id)
                                    .eq('reward_id', redemption.rewardId)
                                    .eq('date', redemption.date);
                                  
                                  if (error) throw error;
                                  
                                  await loadInitialData();
                                  toast.success("Redemption deleted");
                                } catch (error) {
                                  toast.error("Failed to delete redemption");
                                }
                              }}
                            >
                              <Trash className="w-3 h-3" />
                            </Button>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </div>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default AdminDebugPanel; 